import classNames from "classnames";
import React, { useRef, useState } from "react";
import { SelectInput } from "../form/SelectInput";
import _ from 'lodash';
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { useSelector } from "react-redux";
import { selectApiKeys } from "../../store/models/AiOndemandApi";
import { Hosts } from "../../constants";
import { toast } from "react-toastify";
import { Button } from "../Button";
import { CopyIcon } from "../Svg";
import { CreateInferRequestTemplate, CreatePreSignedUrlsTemplate, GetInferRequestTemplate, LanguageChoices, UploadFileToBucketTemplate, UploadFileToServerTemplate, VoiceChoices } from "../../constants/ondemandApi";

const customStyle = {
  ...vscDarkPlus,
  'pre[class*="language-"]': {
    ...vscDarkPlus['pre[class*="language-"]'],
    background: 'rgba(24, 201, 157, 0.1)',
    padding: '25px 20px',
    borderRadius: '6px',
    margin: '0',
    marginTop: '25px'
  },
};

const OndemandApiTab = ({ model, setSelectedTab }) => {
  const sectionRefs = useRef({});
  const apiKeys = useSelector(state => selectApiKeys(state));
  const apiKey = _.get(apiKeys, '0.key');
  const typeOptions = getTypeOptions(model);
  const [curTypeOption, setCurTypeOption] = useState(_.get(typeOptions, '0'));
  const predictionOptions = getPredictionOptions(model);
  const [curPredictionOption, setCurPredictionOption] = useState(_.get(predictionOptions, '0'));
  const sections = getSections(model, curTypeOption, apiKey, setSelectedTab, curPredictionOption);
  const menuItems = getMenuItems(sections)
  const [curItem, setCurItem] = useState(_.get(menuItems, '0'));

  const handleClick = (item) => {
    setCurItem(item);
    if (sectionRefs.current[item.id]) {
      sectionRefs.current[item.id].scrollIntoView({ behavior: "smooth" });
    }
  }

  const renderMenu = (items, classnames) => (
    <ul>
      {items.map((item, i) => {
        const checkActive = item => {
          if (item.id === curItem.id) return true;
          if (item.subitems) {
            for (let subitem of item.subitems) {
              if (subitem.id === curItem.id)
                return true;
            }
          }
          return false;
        }
        return <li key={item.id}>
          <div
            className={classNames(classnames, { 'active': checkActive(item) })}
            onClick={() => handleClick(item)}
            data-number={`${i + 1}.`}
          >
            {item.title}
          </div>
          {item.subitems && (
            <>{renderMenu(item.subitems, 'OnDemandApiTab__left-menu--subtitle')}</>
          )}
        </li>
      })}
    </ul>
  );

  return (
    <div className="OnDemandApiTab">
      <div className="OnDemandApiTab__left-menu">
        <div className="OnDemandApiTab__left-menu--header">
          {`Playground: ${model}`}
        </div>
        <div className="OnDemandApiTab__left-menu--content">
          <div className="OnDemandApiTab__left-menu--table-title">Table of Contents</div>
          <div className="OnDemandApiTab__left-menu--selector">
            <SelectInput
              options={typeOptions}
              value={curTypeOption}
              onChange={setCurTypeOption}
              isDisabled={typeOptions && typeOptions.length < 2} />
          </div>
          {curPredictionOption && <div className="OnDemandApiTab__left-menu--selector">
            <SelectInput
              options={predictionOptions}
              value={curPredictionOption}
              onChange={setCurPredictionOption}
              isDisabled={predictionOptions && predictionOptions.length < 2} />
          </div>}
          {renderMenu(menuItems, 'OnDemandApiTab__left-menu--title')}
        </div>
      </div>

      <div className="OnDemandApiTab__right-content">
        {sections.map((section, i) => (
          <div
            key={section.id}
            ref={(el) => (sectionRefs.current[section.id] = el)}
            className="OnDemandApiTab__right-content--section"
          >
            <div className="OnDemandApiTab__right-content--section-title" data-number={`${i + 1}. `}>
              {section.title}
            </div>
            {section.content && <div className="OnDemandApiTab__right-content--section-content">
              {section.content}
            </div>}
            {section.subitems && (section.subitems.map(item => (<div
              key={item.id}
              ref={(el) => (sectionRefs.current[item.id] = el)}
              className="OnDemandApiTab__right-content--subsection"
            >
              <div className="OnDemandApiTab__right-content--subsection-title">
                {item.title}
              </div>
              <div className="OnDemandApiTab__right-content--subsection-content">
                {item.content}
              </div>
            </div>
            )))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default OndemandApiTab;

function getTypeOptions(name) {
  switch (name) {
    default:
      return [{ label: 'cURL', value: 'curl' },
      { label: 'Javascript / Node.js', value: 'javascript' },
      { label: 'Python', value: 'python' }]
  }
}

function getPredictionOptions(name) {
  switch (name) {
    case 'Image to Image':
      return [{ label: "Art style transfer", value: 'stylize' },
      { label: "Background removal", value: 'background_removal' },
      { label: "Image upscaling", value: 'upscale' }]
    default:
      return [];
  }
}

function getMenuItems(sections) {
  return sections.map(section => {
    const { content, subitems, ...rest } = section;
    return {
      ...rest,
      ...(subitems ? { subitems: getMenuItems(subitems) } : {}),
    };
  });
}

const getSections = (model, curTypeOption, apiKey, setSelectedTab, curPredictionOption) => {
  const type = _.get(curTypeOption, 'value');
  const prediction = _.get(curPredictionOption, 'value');

  const variables = {
    host: Hosts.ONDEMAND_MODEL_API,
    apiKey: '$ON_DEMAND_API_ACCESS_TOKEN',
    requestId: '$REQUEST_ID'
  };
  const isLlm = model === 'Llama 3 8B Instruct'
    || model === 'Llama 3.1 70B Instruct'
    || model === 'DeepSeek R1 / Distill-Qwen-7B';
  const extraFields = isLlm
    ? {}
    : (type === 'curl'
      ? {
        wait: 5,
        "_comment_wait": "(optional) If 'wait' is not set, the backend returns immediately. Otherwise, it waits up to the specified time (e.g. 5 seconds) before responding.",
        webhook: '$WEBHOOK_URL',
        "_comment_webhook": "(optional) Webhook URL to receive the inference result. If provided, the response will be sent to this URL as well."
      } : {
        wait: 5,
        webhook: '$WEBHOOK_URL'
      })
  const baseComment = type === 'curl'
    ? {}
    : isLlm
      ? {}
      : {
        wait: "(optional) If 'wait' is not set, the backend returns immediately. Otherwise, it waits up to the specified time (e.g. 5 seconds) before responding.",
        webhook: "(optional) Webhook URL to receive the inference result. If provided, the response will be sent to this URL as well."
      }
  switch (model) {
    case 'Stable Diffusion Turbo Vision':
      variables.input = generateInput({
        cfg_scale: 8,
        height: 512,
        prompt: "a cute cat",
        sampler_index: "Euler a",
        seed: 1067277458007,
        steps: 25,
        width: 512
      }, type, { ...baseComment }, extraFields);
      variables.service = 'stable_diffusion_turbo_vision';
      break;
    case 'Stable Diffusion XL Turbo':
      variables.input = generateInput({
        guidanceScale: 7,
        prompt: "a cute cat",
        seed: 4775002255836,
        steps: 30,
        strength: 0.8,
      }, type, { ...baseComment }, extraFields);
      variables.service = 'stable_diffusion_xl_turbo';
      break;
    case 'FLUX.1-schnell':
      variables.input = generateInput({
        guidance: 3.5,
        height: 512,
        image2image_strength: 0.8,
        num_steps: 4,
        prompt: "a cute cat",
        seed: "4775002255836",
        width: 512
      }, type, { ...baseComment }, extraFields);
      variables.service = 'flux';
      break;
    case 'Llama 3 8B Instruct':
      variables.input = generateInput({
        max_tokens: 500,
        messages: [{ role: "system", content: "You are a helpfule assistant" },
        { role: "user", content: "What is Theta Network" }],
        stream: true,
        temperature: 0.5,
        top_p: 0.7
      }, curTypeOption, { ...baseComment }, extraFields);
      variables.service = 'llama_3_8b/completions';
      break;
    case 'Llama 3.1 70B Instruct':
      variables.input = generateInput({
        max_tokens: 500,
        messages: [{ role: "system", content: "You are a helpfule assistant" },
        { role: "user", content: "What is Theta Network" }],
        stream: true,
        temperature: 0.5,
        top_p: 0.7
      }, type, { ...baseComment }, extraFields);
      variables.service = 'llama_3_1_70b/completions';
      break;
    case 'DeepSeek R1 / Distill-Qwen-7B':
      variables.input = generateInput({
        max_tokens: 500,
        messages: [{ role: "system", content: "You are a helpfule assistant" },
        { role: "user", content: "What is Theta Network" }],
        stream: true,
        temperature: 0.5,
        top_p: 0.7
      }, type, { ...baseComment }, extraFields);
      variables.service = 'deepseek_r1/completions';
      break;
    case 'Stable Diffusion Video':
      variables.input = generateInput({
        frames_per_second: 16,
        image_filename: "$IMAGE_FILENAME",
        motion_bucket_id: 127,
        seed: 2023635357339779300,
      }, type, {
        ...baseComment,
        image_filename: "It could either be an image filename generated by the preSignedUrl API or an image URL."
      }, extraFields);
      variables.service = 'stable_diffusion_video';
      variables.alias = 'stable_diffusion_video';
      variables.presignedUrl = '$PRESIGNED_URL';
      variables.localFilePath = '$YOUR_FILE_PATH';
      break;
    case 'Whisper':
      variables.input = generateInput({
        audio_filename: "$AUDIO_FILENAME",
      }, type, {
        ...baseComment,
        audio_filename: "it could either be an audio filename generated by the preSignedUrl API or an audio URL."
      }, extraFields);
      variables.service = 'whisper';
      variables.alias = 'whisper';
      variables.presignedUrl = '$PRESIGNED_URL';
      variables.localFilePath = '$YOUR_FILE_PATH';
      break;
    case 'Image to Image':
      variables.service = 'image_to_image';
      variables.alias = 'image_to_image';
      variables.presignedUrl = '$PRESIGNED_URL';
      variables.localFilePath = '$YOUR_FILE_PATH';
      variables.input = generateInput({
        image_filename: "$IMAGE_FILENAME",
      }, type, {
        ...baseComment,
        image_filename: "It could either be an image filename generated by the preSignedUrl API or an image URL."
      }, extraFields);
      if (prediction) {
        variables.service += `/${prediction}`;
        variables.alias += `/${prediction}`;
      }
      if (prediction === 'stylize') {
        variables.input = generateInput({
          image_filename: "$IMAGE_FILENAME",
          style_image_filename: "$STYLE_IMAGE_FILENAME"
        }, type, {
          ...baseComment,
          image_filename: "It could either be an image filename generated by the preSignedUrl API or an image URL.",
          style_image_filename: "It could either be an image filename generated by the preSignedUrl API or an image URL."
        }, extraFields);
      }
      break;
    case 'Talking Head':
      variables.input = generateInput({
        img: "$IMAGE_FILENAME",
        audio: "$AUDIO_FILENAME",
      }, type, {
        ...baseComment,
        img: "it could either be an image filename generated by the preSignedUrl API or an image URL.",
        audio: "it could either be an audio filename generated by the preSignedUrl API or an audio URL."
      }, extraFields);
      variables.service = 'talking_head/generate_video';
      variables.alias = 'talking_head/generate_video';
      variables.presignedUrl = '$PRESIGNED_URL';
      variables.localFilePath = '$YOUR_FILE_PATH';
      break;
    case 'ESRGAN':
      variables.input = generateInput({
        input_img: "$IMAGE_FILENAME",
      }, type, {
        ...baseComment,
        input_img: "it could either be an image filename generated by the preSignedUrl API or an image URL.",
      }, extraFields);
      variables.service = 'esrgan';
      variables.alias = 'esrgan';
      variables.presignedUrl = '$PRESIGNED_URL';
      variables.localFilePath = '$YOUR_FILE_PATH';
      break;
    case 'Blip':
      variables.input = generateInput({
        input_img: "$IMAGE_FILENAME",
      }, type, {
        ...baseComment,
        input_img: "it could either be an image filename generated by the preSignedUrl API or an image URL.",
      }, extraFields);
      variables.service = 'blip';
      variables.alias = 'blip';
      variables.presignedUrl = '$PRESIGNED_URL';
      variables.localFilePath = '$YOUR_FILE_PATH';
      break;
    case 'Grounding Dino':
      variables.input = generateInput({
        input_img: "$IMAGE_FILENAME",
        box_threshold: 0.25,
        text_threshold: 0.25,
        detection_prompt: "$DETECTION_PROMPT"
      }, type, {
        ...baseComment,
        input_img: "it could either be an image filename generated by the preSignedUrl API or an image URL.",
      }, extraFields);
      variables.service = 'grounding_dino';
      variables.alias = 'grounding_dino';
      variables.presignedUrl = '$PRESIGNED_URL';
      variables.localFilePath = '$YOUR_FILE_PATH';
      break;
    case 'Kokoro 82M':
      variables.input = generateInput({
        text: "This is a demo of the Kokoro-82M text-to-speech model.",
        voice: "af_sky",
        speed: 1,
      }, type, {
        ...baseComment,
        voice: `The voice options: ${Object.keys(VoiceChoices)}`
      }, extraFields);
      variables.service = 'kokoro_82m';
      break;
    case 'StableVITON':
      variables.input = generateInput({
        model_img: "$IMAGE_FILENAME",
        garment_img: "$IMAGE_FILENAME",
        step: 20
      }, type, {
        ...baseComment,
        model_img: "it could either be an image filename generated by the preSignedUrl API or an image URL.",
        garment_img: "it could either be an image filename generated by the preSignedUrl API or an image URL.",
      }, extraFields);
      variables.service = 'stable_viton';
      variables.alias = 'stable_viton';
      variables.presignedUrl = '$PRESIGNED_URL';
      variables.localFilePath = '$YOUR_FILE_PATH';
      break;
    case 'StepFun AI / StepVideo':
      variables.input = generateInput({
        prompt: "A golden retriever puppy playing with a red ball in a sunny park",
        negative_prompt: "low contrast, muddy colors, blurry",
        fps: 25,
        frames: 50,
        seed: 100
      }, type, { ...baseComment }, extraFields);
      variables.service = 'step_video';
      break;
    case 'InstantX / InstantID':
      variables.input = generateInput({
        face_img: "$IMAGE_FILENAME",
        pose_img: "$IMAGE_FILENAME",
        prompt: "$PROMPT",
        negative_prompt: "$NEGATIVE_PROMPT",
        adaptor_strength_ratio: 0.8,
        enable_lcm: false,
        enhance_face_region: false,
        guidance_scale: 5,
        identity_strength_ratio: 0.8,
        num_steps: 30,
        seed: 42,
        style_name: "(No style)"
      }, type, {
        ...baseComment,
        face_img: "it could either be an image filename generated by the preSignedUrl API or an image URL.",
        pose_img: "it could either be an image filename generated by the preSignedUrl API or an image URL.",
      }, extraFields);
      variables.service = 'instant_id';
      variables.alias = 'instant_id';
      variables.presignedUrl = '$PRESIGNED_URL';
      variables.localFilePath = '$YOUR_FILE_PATH';
      break;
    case 'Voice Cloning':
      variables.input = generateInput({
        text: "This is a demo of the Voice Cloning model.",
        language: "en",
        voice: "AUDIO_FILENAME",
      }, type, {
        ...baseComment,
        voice: "it could either be an audio filename generated by the preSignedUrl API or an audio URL.",
        language: `The language options: ${Object.keys(LanguageChoices)}`
      }, extraFields);
      variables.service = 'voice_cloning';
      variables.alias = 'voice_cloning';
      variables.presignedUrl = '$PRESIGNED_URL';
      variables.localFilePath = '$YOUR_FILE_PATH';
      break;
    default:
      break;
  }

  const createCommand = substituteCommand(_.get(CreateInferRequestTemplate, type), variables);
  const getCommand = substituteCommand(_.get(GetInferRequestTemplate, type), variables);

  const handleCopyCommand = (command) => {
    navigator.clipboard.writeText(command);
    toast.success("Command copied to clipboard!");
  };


  const sections = [
    {
      id: "calling-api",
      title: "Calling the API",
      subitems: [
        {
          id: "create-infer-request",
          title: "Create Infer Request",
          content: (<>
            <p>Create an Infer Request.</p>
            <p>The backend will wait up to 60 seconds for the infer request to complete or return an error if a wait parameter is included in the request body.</p>
            <p>Please click{' '}
              <span onClick={() => setSelectedTab('token')} className="tab-link">
                here
              </span> to create an on-demand API access token. Note that an access token can be used for all on-demand model APIs.
            </p>
            <div className="code-wrapper">
              <SyntaxHighlighter
                style={customStyle}
                language={type === 'curl' ? 'bash' : type}>
                {createCommand}
              </SyntaxHighlighter>
              <div className="code-copy-button"
                onClick={() => handleCopyCommand(createCommand)} >
                <CopyIcon />
              </div>
            </div>
          </>
          ),
        },
        {
          id: "get-infer-request",
          title: "Get Infer Request",
          content: (<>
            <p>
              Get an infer request status and output using the infer request ID created above.
            </p>
            <div className="code-wrapper">
              <SyntaxHighlighter
                style={customStyle}
                language={type === 'curl' ? 'bash' : type}>
                {getCommand}
              </SyntaxHighlighter>
              <div className="code-copy-button"
                onClick={() => handleCopyCommand(getCommand)} >
                <CopyIcon />
              </div>
            </div>
          </>
          ),
        },
      ]
    },
    {
      id: "authentication",
      title: "Authentication",
      content: <>
        <p>Authentication is done using an API Key. Make sure to keep it secure.</p>
        {apiKey
          ? <p>You can manage and edit your API keys{' '}
            <span onClick={() => setSelectedTab('token')} className="tab-link">
              here
            </span>
            .</p>
          : <p>You don't have an API key yet. Navigate to the{' '}
            <span onClick={() => setSelectedTab('token')} className="tab-link">
              Access Token
            </span>
            {' '}tab to create one.</p>
        }
      </>,
    },
  ];
  switch (model) {
    case 'Llama 3 8B Instruct':
    case 'Llama 3.1 70B Instruct':
    case 'DeepSeek R1 / Distill-Qwen-7B':
      sections[0].subitems.splice(1);
      return sections;
    case 'Stable Diffusion Video':
    case 'Whisper':
    case 'Image to Image':
    case 'Talking Head':
    case 'ESRGAN':
    case 'Blip':
    case 'Grounding Dino':
    case 'StableVITON':
    case 'InstantX / InstantID':
    case 'Voice Cloning':
      const presignedUrlCommand = substituteCommand(_.get(CreatePreSignedUrlsTemplate, type), variables);
      const uploadFileCommand = substituteCommand(_.get(UploadFileToBucketTemplate, type), variables);
      const newSubItems = [{
        id: "create-input-presigned-urls",
        title: "Create Input Presigned URLs (*optional)",
        content: (<>
          <p>
            Retrieve a filename for use in the create infer request input, along with a URL for later file upload.
          </p>
          <div className="code-wrapper">
            <SyntaxHighlighter
              style={customStyle}
              language={type === 'curl' ? 'bash' : type}>
              {presignedUrlCommand}
            </SyntaxHighlighter>
            <div className="code-copy-button"
              onClick={() => handleCopyCommand(presignedUrlCommand)} >
              <CopyIcon />
            </div>
          </div>
        </>
        )
      }, {
        id: "upload-file-to-bucket",
        title: "Upload File To Bucket (*optional)",
        content: (<>
          <p>
            Upload the local file to the pre-signed URL.
          </p>
          <div className="code-wrapper">
            <SyntaxHighlighter
              style={customStyle}
              language={type === 'curl' ? 'bash' : type}>
              {uploadFileCommand}
            </SyntaxHighlighter>
            <div className="code-copy-button"
              onClick={() => handleCopyCommand(uploadFileCommand)} >
              <CopyIcon />
            </div>
          </div>
        </>
        )
      }]
      sections[0].subitems = sections[0].subitems.concat(newSubItems);
      return sections;
    default:
      return sections;
  }
}


const generateInput = (inputParams, type = 'curl', comments = {}, extraFields = {}) => {
  let result = `{\n  "input": {\n`;
  Object.entries(inputParams).forEach(([key, value], index, array) => {
    const isArrayOrObject = typeof value === "object";
    result += `    "${key}": ${isArrayOrObject
      ? JSON.stringify(value, null, 6)
      : typeof value === "string" ? `"${value}"` : value}`;
    if (index < array.length - 1) {
      result += ",";
    }
    if (comments[key]) {
      switch (type) {
        case 'javascript':
          result += ` // ${comments[key]}`;
          break;
        case 'python':
        case 'curl':
          result += ` # ${comments[key]}`;
          break;
        default:
          break;
      }
    }
    result += "\n";
  });
  result += "  }";

  Object.entries(extraFields).forEach(([key, value]) => {
    const isArrayOrObject = typeof value === "object";
    result += `,\n  "${key}": ${isArrayOrObject
      ? JSON.stringify(value, null, 6)
      : typeof value === "string" ? `"${value}"` : value}`;
    if (comments[key]) {
      switch (type) {
        case 'javascript':
          result += ` // ${comments[key]}`;
          break;
        case 'python':
        case 'curl':
          result += ` # ${comments[key]}`;
          break;
        default:
          break;
      }
    }
  });

  result += "\n}";
  return result;
};

const substituteCommand = (template, variables) => {
  const skipList = ['response.status', 'response.statusText'];
  return template.replace(/\${(.*?)}/g, (_, key) => {
    if (skipList.includes(key)) {
      return '${' + key + '}';
    }
    return variables[key] || '';
  });
};